import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Store, AlertCircle, MapPin, ArrowRight } from "lucide-react";

const WAREHOUSE_IMG = "https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700;800;900&family=IBM+Plex+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@500;600&display=swap');
.lg-root { font-family: 'IBM Plex Sans', system-ui, sans-serif; }
.lg-display { font-family: 'Barlow Condensed', sans-serif; }
.lg-mono { font-family: 'JetBrains Mono', monospace; }
@keyframes lg-up { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
@keyframes lg-pop { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
.lg-up { animation: lg-up .5s cubic-bezier(.16,1,.3,1) both; }
.lg-pop { animation: lg-pop .3s ease both; }
@keyframes lg-pulse { 0%,100% { opacity: 1; } 50% { opacity: .35; } }
.lg-pulse { animation: lg-pulse 1.8s ease-in-out infinite; }
`;

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      navigate(u?.role === "dangoods" ? "/dangoods" : u?.role === "tradepartner" ? "/tradepartner" : u?.role === "stockgroup" ? "/stockgroup" : "/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lg-root min-h-screen grid grid-cols-1 lg:grid-cols-12 bg-[#0D0F12] text-slate-100 antialiased" style={{ WebkitTapHighlightColor: "transparent" }}>
      <style>{CSS}</style>

      {/* Left: form */}
      <div className="lg:col-span-6 xl:col-span-5 flex flex-col justify-between px-6 py-10 sm:px-12 lg:px-16 relative z-20 border-r border-white/5">
        {/* Brand */}
        <div className="flex items-center gap-2.5 lg-up">
          <div className="w-10 h-10 bg-[#C8102E] flex items-center justify-center rounded-lg shadow-lg shadow-[#C8102E]/30">
            <Store className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div className="leading-none">
            <span className="lg-display block text-xl font-extrabold uppercase tracking-wider">Lagersalg</span>
            <span className="lg-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Stats Portal</span>
          </div>
        </div>

        {/* Center block */}
        <div className="w-full max-w-md mx-auto lg:mx-0 py-10">
          <p className="lg-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-[#C8102E] mb-3 lg-up">KPI Dashboard</p>
          <h1 className="lg-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-[0.95] mb-3 lg-up">
            Log ind
          </h1>
          <p className="text-sm text-slate-400 mb-8 lg-up">Samlet salgsoverblik for Falster &amp; Ringsted.</p>

          {/* Form card */}
          <div className="relative bg-[#14171D] border border-white/10 p-6 sm:p-7 rounded-2xl shadow-2xl overflow-hidden lg-up">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#C8102E] via-[#FFC72C] to-[#C8102E]" />
            <form onSubmit={submit} className="space-y-5" data-testid="login-form">
              <div className="space-y-1.5">
                <label htmlFor="email" className="lg-mono text-[11px] font-semibold uppercase tracking-widest text-slate-400">E-mail</label>
                <Input
                  id="email" type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="din@email.dk"
                  className="h-12 bg-[#0A0C0F] border-white/15 rounded-xl px-4 text-slate-100 placeholder:text-slate-600 focus-visible:ring-2 focus-visible:ring-[#C8102E] focus-visible:border-transparent"
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="password" className="lg-mono text-[11px] font-semibold uppercase tracking-widest text-slate-400">Adgangskode</label>
                <Input
                  id="password" type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 bg-[#0A0C0F] border-white/15 rounded-xl px-4 text-slate-100 placeholder:text-slate-600 focus-visible:ring-2 focus-visible:ring-[#C8102E] focus-visible:border-transparent"
                  data-testid="login-password-input"
                />
              </div>

              {error && (
                <div className="flex items-start gap-2.5 text-sm text-red-200 bg-red-950/60 border border-red-500/40 p-3.5 rounded-xl lg-pop" data-testid="login-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button
                type="submit" disabled={loading}
                className="w-full h-12 bg-[#C8102E] hover:bg-[#A50D24] text-white lg-display text-base font-bold tracking-wider uppercase rounded-xl transition-all shadow-lg shadow-[#C8102E]/20 hover:shadow-[#C8102E]/40 active:scale-[0.99] flex items-center justify-center gap-2 border border-red-500/30"
                data-testid="login-submit-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (<>Log ind <ArrowRight className="w-4 h-4" /></>)}
              </Button>
            </form>
          </div>
        </div>

        <p className="lg-mono text-[10px] uppercase tracking-[0.2em] text-slate-600 text-center lg:text-left">© Lagersalg · Customers First</p>
      </div>

      {/* Right: brand panel */}
      <div className="hidden lg:col-span-6 xl:col-span-7 lg:block relative overflow-hidden border-l border-white/5">
        <img src={WAREHOUSE_IMG} alt="Lager" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0D0F12] via-[#0D0F12]/80 to-[#C8102E]/25" />
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #fff 1.5px, transparent 1.5px)", backgroundSize: "30px 30px" }} />

        <div className="relative z-10 flex flex-col justify-between h-full p-12 xl:p-20">
          <div className="flex items-center gap-2 text-white/60">
            <span className="w-2 h-2 rounded-full bg-emerald-400 lg-pulse" />
            <span className="lg-mono text-[11px] uppercase tracking-[0.25em]">Driftstatus · 2 butikker online</span>
          </div>

          <div>
            <h2 className="lg-display text-5xl xl:text-6xl font-black uppercase leading-[0.9] tracking-tight mb-6">
              Alt dit salg.<br /><span className="text-[#FFC72C]">Ét sted.</span>
            </h2>
            <p className="text-white/70 max-w-md leading-relaxed text-sm">
              Sammenlign omsætning, ordrer og gennemsnitlig kurv på tværs af Falster og Ringsted — i realtid.
            </p>

            <div className="flex gap-3 mt-10">
              {[["Falster", "Butik #1", "#C8102E"], ["Ringsted", "Butik #2", "#FFC72C"]].map(([name, sub, acc]) => (
                <div key={name} className="flex items-center gap-2.5 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl px-4 py-3">
                  <MapPin className="w-4 h-4" style={{ color: acc }} />
                  <div className="leading-tight">
                    <p className="lg-display text-sm font-bold uppercase tracking-wide">{name}</p>
                    <p className="lg-mono text-[10px] uppercase tracking-widest text-white/50">{sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
